import STATE_ABBREVIATIONS from '../constants/stateAbbreviations';
import getCleanFirstStreetWord from '../helpers/properties/getCleanFirstStreetWord';
import { RepliersDataCleaner } from '../utils/RepliersDataCleaner';
import isEqual from 'lodash.isequal';
import isValidCanadianZipCode from '../helpers/addresses/isValidCanadianZipCode';
import sanitizeZip from '../helpers/addresses/sanitizeZip';
import { TWO_NUMBERS_RANGE_REGEX } from '../constants/regex';

export type AddressObject = {
  area: string;
  zip: null | string;
  postalFsa?: null | string;
  country: null | string;
  city: string;
  streetNumber: string;
  unitNumber: null | string;
  streetDirection: string;
  streetName: string;
  district: string;
  streetSuffix: string;
  neighborhood: string;
  state: string;
  majorIntersection: string;
  communityCode: string;
};

export default class Address {
  area: string;
  zip: null | string;
  postalFsa?: null | string; // the first three characters of a postal code are called the Forward Sorting Area (FSA)
  country: null | string;
  city: string;
  streetNumber: string;
  unitNumber: null | string;
  streetDirection: string;
  streetName: string;
  district: string;
  streetSuffix: string;
  neighborhood: string;
  state: string;
  majorIntersection: string;
  communityCode: string;

  static fromRepliersAddress(repliersAddress: AddressObject, resource: string) {
    return new Address({
      ...repliersAddress,
      zip: sanitizeZip(repliersAddress.zip),
      streetNumber: RepliersDataCleaner.cleanStreetNumber(
        repliersAddress.streetNumber,
      ),
      unitNumber: RepliersDataCleaner.cleanUnitNumber(
        repliersAddress.unitNumber,
      ),
    });
  }

  static fromObject(addressObject: AddressObject) {
    return new Address(addressObject);
  }

  constructor({
    area,
    zip,
    postalFsa,
    country,
    city,
    streetNumber,
    unitNumber,
    streetDirection,
    streetName,
    district,
    streetSuffix,
    neighborhood,
    state,
    majorIntersection,
    communityCode,
  }: AddressObject) {
    this.area = area;
    this.zip = zip;
    this.postalFsa = postalFsa;
    this.country = country;
    this.city = city;
    this.streetNumber = streetNumber;
    this.unitNumber = unitNumber;
    this.streetDirection = streetDirection;
    this.streetName = streetName;
    this.district = district;
    this.streetSuffix = streetSuffix;
    this.neighborhood = neighborhood;
    this.state = state;
    this.majorIntersection = majorIntersection;
    this.communityCode = communityCode;
  }

  abbreviateState(): string {
    return STATE_ABBREVIATIONS[this.state] || '';
  }

  // Default: 55 Harvie Ave W
  toString({
    streetNumber = true,
    unitNumber = false,
    city = false,
    state = false,
    abbreviatedState = false,
    zip = false,
    zipNoSpace = false,
    fsa = false,
  } = {}): string {
    let addressString = '';
    if (streetNumber && this.streetNumber) {
      addressString += `${this.streetNumber} `;
    }
    addressString += `${this.streetName}`;
    if (this.streetSuffix) {
      addressString += ` ${this.streetSuffix}`;
    }
    if (this.streetDirection) {
      addressString += ` ${this.streetDirection}`;
    }
    if (unitNumber && this.unitNumber) {
      addressString += ` #${this.unitNumber}`;
    }
    if (city && this.city) {
      addressString += `, ${this.city}`;
    }
    if ((state || abbreviatedState) && this.state) {
      if (abbreviatedState && this.abbreviateState()) {
        addressString += `, ${this.abbreviateState()}`;
      } else {
        addressString += `, ${this.state}`;
      }
    }
    if (zip && this.zip) {
      addressString += `, ${this.zip}`;
    } else if (zipNoSpace && this.zip) {
      addressString += `, ${this.zip.replace(' ', '')}`;
    } else if (fsa && this.fsa()) {
      addressString += `, ${this.fsa()}`;
    }
    return addressString;
  }

  toLocationString(): string {
    return this.toString();
  }

  // 55 Harvie Ave W #24
  toPropertyString(): string {
    return this.toString({ unitNumber: true });
  }

  // 24-55 Harvie Ave W
  formattedMailingStreetAddress(): string {
    return `${this.unitNumber ? `${this.unitNumber}-` : ''}${this.toString()}`;
  }

  // 55 Harvie Ave W #24, Toronto
  formattedAddressShortOneLiner(): string {
    return this.toString({
      unitNumber: true,
      city: true,
    });
  }

  formattedAddressArray(): string[] {
    const streetLine = this.toString({ unitNumber: true });
    const cityLine = `${this.city}, ${this.abbreviateState()}`;

    return [streetLine, cityLine];
  }

  formattedAddressParagraph(): string {
    return this.formattedAddressArray().join('\n');
  }

  formattedFullMailingAddress(): string {
    return `${this.formattedMailingStreetAddress()}, ${this.city}, ${
      this.state
    }${this.zip ? ` ${this.zip}` : ''}`;
  }

  withoutUnitNumber(): string {
    return (
      this.streetNumber +
      this.streetName +
      this.streetSuffix +
      this.streetDirection +
      this.city
    );
  }

  locationZipSignature(): string | null {
    if (!isValidCanadianZipCode(this.zip)) {
      // this is not a legit zip code, so we don't want to include it as a part of the signature, otherwise we're bound to get a false match
      return null;
    }
    return `${this.streetNumber} ${this.zip} ${this.cleanFirstStreetWord()}`;
  }

  cleanFirstStreetWord(): string {
    return getCleanFirstStreetWord(this.streetName);
  }

  isDifferentThan(otherAddress: Address): boolean {
    return !isEqual(this, otherAddress);
  }

  streetNumberNumeric(): string {
    const NOT_DIGIT_REGEX = /\D/g;
    return this.streetNumber.replace(NOT_DIGIT_REGEX, '');
  }

  searchableAddress(): string {
    let address = `${this.searchableStreetNumber()} `;
    address += this.toString({
      streetNumber: false,
      zip: true,
      fsa: true,
      city: true,
      state: true,
    });
    return address;
  }

  searchableStreetNumber(): string {
    if (this.streetNumber.match(TWO_NUMBERS_RANGE_REGEX)) {
      const twoNumbersRangeMatch = this.streetNumber.match(
        TWO_NUMBERS_RANGE_REGEX,
      );
      return twoNumbersRangeMatch[2];
    }

    const FIRST_NUMBER_REGEX = /(\d+)/;
    if (this.streetNumber.match(FIRST_NUMBER_REGEX)) {
      const firstNumberMatch = this.streetNumber.match(FIRST_NUMBER_REGEX);
      return firstNumberMatch[1];
    }

    return this.streetNumber;
  }

  fsa(): string | null {
    if (this.postalFsa) {
      return this.postalFsa;
    } else if (this.zip) {
      return this.zip.substring(0, 3);
    }
    return null;
  }

  toObject(): AddressObject {
    return {
      area: this.area,
      zip: this.zip,
      postalFsa: this.postalFsa,
      country: this.country,
      city: this.city,
      streetNumber: this.streetNumber,
      unitNumber: this.unitNumber,
      streetDirection: this.streetDirection,
      streetName: this.streetName,
      district: this.district,
      streetSuffix: this.streetSuffix,
      neighborhood: this.neighborhood,
      state: this.state,
      majorIntersection: this.majorIntersection,
      communityCode: this.communityCode,
    };
  }
}
